/* @flow */

import * as React from 'react';
import numeral from 'numeral';
import colorUtils from 'color';
import styled from '@emotion/styled';
import { padding } from 'polished';

import { hover } from 'app/utils/cssUtils';
import BlockSpinner from 'app/components/commons/BlockSpinner';
import PlayerBar from 'app/components/challenge/PlayerBar';
import PlayerBarValue from 'app/components/challenge/PlayerBarValue';
import RowButton from 'app/components/commons/RowButton';
import RowSuffix from 'app/components/commons/RowSuffix';
import Tappable from 'app/components/commons/Tappable';
import PlayerAvatar from './PlayerAvatar';
import PlayerLabels from './PlayerLabels';
import StarSymbol from './StarSymbol';

type Props = {
  activeBonus?: string,
  awayTeam?: string,
  bonus?: {
    name: string,
    id: string
  },
  gameOrder?: number,
  highlight?: boolean,
  homeTeam?: string,
  gameId?: string,
  id: string,
  idStr: string,
  image?: string,
  initials?: string,
  isAnimating?: boolean,
  isHome: boolean,
  isRacing?: boolean,
  isScratched?: boolean,
  labels: Array<Object>,
  locked?: boolean,
  name: string,
  number?: number,
  place?: number,
  popularity?: number,
  positionId: string,
  positionLabel: string,
  onBonusAdd?: (key: string, playerId: string) => void,
  onLock?: (string, string) => void,
  onRemove?: (string, string) => void,
  onUnlock?: (string, string) => void,
  onWidgetClick?: (string, string) => void,
  reverseOrder?: boolean,
  rider?: string,
  riderImage?: string,
  salary?: number,
  showButtons: boolean,
  trainer?: string,
  win?: number
};

const SelectedEntityRowWrapper = styled.div(
  ({ theme }) => ({
    backgroundColor: theme.color.white,
    borderColor: theme.color.basis6,
    borderStyle: 'solid',
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    color: theme.color.basis1,
    transition: 'background-color 0.2s'
  }),
  ({ disabled, theme }) =>
    disabled
      ? {}
      : {
          ...hover({
            backgroundColor: colorUtils(theme.color.white)
              .darken(0.05)
              .hex()
          })
        }
);

const RowInner = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexBasis: 0,
  flexDirection: 'row',
  flexGrow: 1,
  flexShrink: 1,
  fontSize: '0.9rem',
  minWidth: 0, // flexbox hack
  paddingLeft: 5
});

const AvatarCell = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  ...padding(null, 5),
  position: 'relative'
});

const RowEnd = styled.div({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'row',
  flexGrow: 0,
  flexShrink: 0,
  height: '100%',
  minWidth: 0 // flexbox hack
});

const BlockSpinnerContainer = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  ...padding(4, 0),
  width: '2.5rem',
  justifyContent: 'center',
  color: theme.color.primary
}));

const Stack = styled.div({
  display: 'flex',
  flexDirection: 'column',
  paddingRight: '0.5em',
  alignItems: 'flex-end'
});

const ValueLabel = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold,
  paddingRight: theme.spacing.xsmall
}));

class SelectedEntityRow extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    activeBonus: undefined,
    awayTeam: '',
    bonus: undefined,
    gameId: undefined,
    gameOrder: undefined,
    highlight: false,
    homeTeam: '',
    image: undefined,
    initials: undefined,
    isAnimating: false,
    isRacing: false,
    isScratched: false,
    locked: false,
    number: undefined,
    onBonusAdd: undefined,
    onLock: undefined,
    onRemove: undefined,
    onUnlock: undefined,
    onWidgetClick: undefined,
    place: undefined,
    popularity: undefined,
    reverseOrder: false,
    rider: undefined,
    riderImage: undefined,
    salary: undefined,
    trainer: undefined,
    win: undefined
  };

  handleBonusAdd = () => {
    const { activeBonus, id, onBonusAdd } = this.props;
    if (activeBonus && onBonusAdd) {
      onBonusAdd(activeBonus, id);
    }
  };

  handleRemove = () => {
    const { idStr, onRemove, positionId } = this.props;
    if (onRemove) {
      onRemove(idStr, positionId);
    }
  };

  handleLock = () => {
    const { idStr, locked, onLock, onUnlock, positionId } = this.props;
    if (locked) {
      onUnlock(idStr, positionId);
    } else {
      onLock(idStr, positionId);
    }
  };

  handleWidgetClick = () => {
    const { idStr, isRacing, onWidgetClick, gameId, positionId } = this.props;
    onWidgetClick(idStr, isRacing ? gameId : positionId);
  };

  render() {
    const {
      activeBonus,
      awayTeam,
      bonus,
      gameOrder,
      highlight,
      homeTeam,
      image,
      initials,
      isAnimating,
      isHome,
      isRacing,
      isScratched,
      labels,
      locked,
      number,
      onWidgetClick,
      name,
      place,
      positionId,
      popularity,
      positionLabel,
      reverseOrder,
      rider,
      riderImage,
      salary,
      showButtons,
      trainer,
      win
    } = this.props;

    const avatarImage = isRacing ? riderImage : image;

    return (
      <div>
        <Tappable
          component={SelectedEntityRowWrapper}
          disabled={!onWidgetClick}
          onTap={onWidgetClick ? this.handleWidgetClick : undefined}
        >
          <RowInner>
            {(avatarImage || initials) && (
              <AvatarCell>
                <PlayerAvatar fallbackText={initials} image={avatarImage} />
              </AvatarCell>
            )}
            <PlayerBar
              awayTeam={awayTeam}
              bonus={bonus}
              highlight={highlight}
              homeTeam={homeTeam}
              gameOrder={gameOrder}
              isCaptain={positionId === 'captain'}
              isHome={isHome}
              isRacing={isRacing}
              isScratched={isScratched}
              labels={isRacing ? labels : undefined}
              name={name}
              number={number}
              position={positionLabel}
              popularity={popularity}
              reverseOrder={reverseOrder}
              rider={rider}
              trainer={trainer}
            />
            {isRacing ? (
              <Stack>
                <PlayerBarValue>
                  <ValueLabel>W</ValueLabel>
                  <span>{numeral(win).format('0.00')}</span>
                  <StarSymbol />
                </PlayerBarValue>
                <PlayerBarValue>
                  <ValueLabel>P</ValueLabel>
                  <span>{numeral(place).format('0.00')}</span>
                  <StarSymbol />
                </PlayerBarValue>
              </Stack>
            ) : (
              <Stack>
                <PlayerBarValue>{numeral(salary).format('$0,0')}</PlayerBarValue>
                <PlayerLabels items={labels} />
              </Stack>
            )}
            {showButtons && (
              <RowEnd>
                <RowSuffix>
                  {isAnimating && !locked ? (
                    <BlockSpinnerContainer>
                      <BlockSpinner />
                    </BlockSpinnerContainer>
                  ) : (
                    <RowButton type={locked ? 'lock' : 'unlock'} onSelect={this.handleLock} />
                  )}
                  {activeBonus ? (
                    <RowButton type={activeBonus} onSelect={this.handleBonusAdd} />
                  ) : (
                    <RowButton type="remove" onSelect={this.handleRemove} />
                  )}
                </RowSuffix>
              </RowEnd>
            )}
          </RowInner>
        </Tappable>
      </div>
    );
  }
}

export default SelectedEntityRow;
